import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-regular-svg-icons";
import { usePageVisibility } from "react-page-visibility";
import { CashIcon } from "@heroicons/react/solid";
import { createAvatar } from "@dicebear/core";
import { miniavs } from "@dicebear/collection";

// import { useLocation } from 'react-router-dom'
import {
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import AuthContext from "../context/AuthProvider";
import { useNavigate, useLocation, Link } from "react-router-dom";
import useLogout from "../hooks/useLogout";
import axios from "axios";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useRefreshToken from "../hooks/useRefreshToken";
import { useInterval } from "../utils/useInterval";
import FlipNumbers from "react-flip-numbers";
import Notifications from "./Notif/Notifications";
import HeaderAvatar from "./Visual/HeaderAvatar";
import {
  getPushState,
  isIOS,
  requestPushPermission,
  syncPushSubscription,
} from "../utils/pushNotifications";

function removeNumberInParentheses(string) {
  const regex = /\(\d+\)/g;
  return string.replace(regex, "");
}

const Header = () => {
  const {
    auth,
    credits,
    setCredits,
    checking,
    setChecking,
    avatarUploaded,
    notifLength,
    setNotifLength,
  } = useContext(AuthContext);
  const isVisible = usePageVisibility();
  const [sticky, setSticky] = useState("");

  const axiosPrivate = useAxiosPrivate();
  const refresh = useRefreshToken();
  const location = useLocation();
  const [showNotif, setShowNotif] = useState(false);
  const [fetchingUpdates, setFetchingUpdates] = useState(false);
  const [showExtraMenu, setShowExtraMenu] = useState(false);

  const [updates, setUpdates] = useState([]);
  const latestRequestRef = useRef(null);
  const latestAbortRef = useRef(null);
  const summaryRequestRef = useRef(null);
  const summaryAbortRef = useRef(null);
  const latestSummaryRevisionRef = useRef(null);
  const subscriptionSyncInFlightRef = useRef(false);
  const [pullChange, setPullChange] = useState(0);
  const refreshCont = useRef(0);
  const startPointRef = useRef(0);
  const pullDistanceRef = useRef(0);
  const isPullingRef = useRef(false);
  const PULL_THRESHOLD = 220;
  const pullProgress = Math.min(pullChange / PULL_THRESHOLD, 1);
  const pullOpacity =
    pullChange > 0 ? Math.min(0.35 + pullProgress * 0.65, 1) : 0;
  const pullRotation = pullProgress * 210;
  const pullScale = 0.9 + pullProgress * 0.25;
  const pullHint =
    pullProgress >= 1 ? "Αφήστε για ανανέωση" : "Σύρετε για ανανέωση";

  const closeNotif = () => {
    setShowNotif(false);
  };

  // On app resume (visibility/focus) attempt a silent refresh if persist=true and no access token
  const { persist } = useContext(AuthContext);
  useEffect(() => {
    const handleVisible = () => {
      if (persist && !auth?.accessToken) {
        refresh().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisible);
    window.addEventListener("focus", handleVisible);
    return () => {
      document.removeEventListener("visibilitychange", handleVisible);
      window.removeEventListener("focus", handleVisible);
    };
  }, [persist, auth?.accessToken, refresh]);

  // listen to pageshow event

  useEffect(() => {
    if (!navigator.setAppBadge) return;
    // Rejects on iOS until notifications are allowed; nothing to do then.
    try {
      Promise.resolve(navigator.setAppBadge(notifLength)).catch(() => {});
    } catch {}
  }, [notifLength]);

  const initLoading = useCallback(() => {
    if (!refreshCont.current) {
      return;
    }
    refreshCont.current.classList.add("pull-loading");
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }, []);

  const pullStart = useCallback(
    (e) => {
      if (window.scrollY > 0 || !e.targetTouches || e.targetTouches.length !== 1) {
        isPullingRef.current = false;
        return;
      }
      const { clientY } = e.targetTouches[0];
      isPullingRef.current = true;
      startPointRef.current = clientY;
      pullDistanceRef.current = 0;
      setPullChange(0);
    },
    [setPullChange]
  );

  const pull = useCallback(
    (e) => {
      if (!isPullingRef.current || !e.targetTouches || e.targetTouches.length !== 1) {
        return;
      }
      if (window.scrollY > 0) {
        isPullingRef.current = false;
        startPointRef.current = 0;
        pullDistanceRef.current = 0;
        setPullChange(0);
        return;
      }
      const { clientY } = e.targetTouches[0];
      const pullLength =
        clientY > startPointRef.current ? clientY - startPointRef.current : 0;
      const limitedPullLength = Math.min(pullLength, PULL_THRESHOLD * 1.35);
      pullDistanceRef.current = limitedPullLength;
      setPullChange(limitedPullLength);
    },
    [setPullChange, PULL_THRESHOLD]
  );

  const endPull = useCallback(() => {
    if (!isPullingRef.current) {
      return;
    }
    isPullingRef.current = false;
    if (pullDistanceRef.current >= PULL_THRESHOLD) {
      initLoading();
    }
    pullDistanceRef.current = 0;
    startPointRef.current = 0;
    setPullChange(0);
  }, [initLoading, setPullChange, PULL_THRESHOLD]);

  useEffect(() => {
    window.addEventListener("touchstart", pullStart);
    window.addEventListener("touchmove", pull);
    window.addEventListener("touchend", endPull);
    window.addEventListener("touchcancel", endPull);
    return () => {
      window.removeEventListener("touchstart", pullStart);
      window.removeEventListener("touchmove", pull);
      window.removeEventListener("touchend", endPull);
      window.removeEventListener("touchcancel", endPull);
    };
  }, [pullStart, pull, endPull]);

  useEffect(() => {
    const hY = document.getElementById("user-header").offsetTop;
    localStorage.setItem("headerY", hY);
    window.addEventListener("scroll", isSticky);
    return () => {
      window.removeEventListener("scroll", isSticky);
    };
  }, []);
  const isSticky = () => {
    /* Method that will fix header after a specific scrollable */

    const stickyClass =
      window.scrollY >= localStorage.getItem("headerY") ? "is-sticky" : "";
    setSticky(stickyClass);
  };
  const classes = `user-header ${sticky}`;
  const user_classes = `user ${sticky}`;

  const getSeen = async () => {
    try {
      const response = await axiosPrivate.get("/updates/latest/seen");
      setNotifLength(0);
    } catch (err) {
      console.error(err);
    }
  };

  const handleShowNotif = async () => {
    const willOpen = !showNotif;
    setShowNotif(willOpen);
    if (!willOpen) return;
    const summaryResult = await fetchLatestSummary();
    if (summaryResult?.revisionChanged || updates.length === 0) {
      await fetchLatestUpdates();
    }
  };
  const navigate = useNavigate();
  const logout = useLogout();

  const signOut = async () => {
    localStorage.clear();
    await logout();
    navigate("/");
  };
  const avatar = useMemo(() => {
    return createAvatar(miniavs, {
      size: 16,
      seed: auth?.uuid,
      backgroundType: ["gradientLinear", "solid"],
      backgroundColor: ["b6e3f4", "c0aede", "d1d4f9"],
      // ... other options
    }).toDataUriSync();
  }, [auth?.uuid]);
  useEffect(() => {
    if (location.pathname === "/" && auth?.user) {
      document.title = "MySolon.gr";
    }
    if (location.pathname === "/astika" && auth?.user) {
      document.title = "Αστικά | MySolon.gr";
    }
    if (location.pathname === "/out" && auth?.user) {
      document.title = "Αστικά(Εκτός Σόλων) | MySolon.gr";
    }
    if (location.pathname === "/adjustice" && auth?.user) {
      document.title = "Διοικητικά | MySolon.gr";
    }

    if (location.pathname === "/imerologio" && auth?.user) {
      document.title = "Ημερολόγιο | MySolon.gr";
    }
    if (location.pathname === "/eauction" && auth?.user) {
      document.title = "Eauction | MySolon.gr";
    }
    if (location.pathname === "/case-stats" && auth?.user) {
      document.title = "Στατιστικά Υποθέσεων | MySolon.gr";
    }
    if (location.pathname === "/poinika" && auth?.user) {
      document.title = "Πινάκια Ποινικών | MySolon.gr";
    }
    if (location.pathname === "/etairies" && auth?.user) {
      document.title = "Εταιρίες | MySolon.gr";
    }
    if (location.pathname === "/updates" && auth?.user) {
      document.title = "Επισκόπηση | MySolon.gr";
    }
    if (location.pathname === "/calendar" && auth?.user) {
      document.title = "Ημερολόγιο | MySolon.gr";
    }
    if (location.pathname === "/prothesmies/neataktiki" && !auth?.user) {
      document.title = "Υπολογισμός Προθεσμιών Νέας Τακτικής | MySolon.gr";
    }
    if (location.pathname === "/prothesmies/mikrodiafores" && !auth?.user) {
      document.title = "Υπολογισμός Προθεσμιών Μικροδιαφορών | MySolon.gr";
    }
    if (location.pathname === "/prothesmies/ste" && !auth?.user) {
      document.title =
        "Υπολογισμός Προθεσμιών Νέας Δικονομίας ΣτΕ | MySolon.gr";
    }
    if (location.pathname === "/prothesmies/diamesolavisi" && !auth?.user) {
      document.title = "Υπολογισμός Προθεσμιών Διαμεσολάβησης | MySolon.gr";
    }
    if (location.pathname === "/prothesmies/diatpliromis" && !auth?.user) {
      document.title = "Υπολογισμός Προθεσμιών Διαταγών Πληρωμής | MySolon.gr";
    }
    if (location.pathname === "/prothesmies/apopoiisis" && !auth?.user) {
      document.title =
        "Υπολογισμός Προθεσμιών Αποποίησης Κληρονομίας | MySolon.gr";
    }
    if (notifLength !== 0 && document.title.indexOf(")") === -1) {
      document.title = `(${notifLength}) ${document.title}`;
    }
    if (notifLength === 0 && document.title.indexOf(")") !== -1) {
      // Remove () from title
      document.title = removeNumberInParentheses(document.title);
    }
  }, [location, updates, notifLength, showNotif, auth?.user]);
  useEffect(() => {
    if (!auth?.user) {
      return;
    }

    const registerForPushNotifications = async () => {
      if (subscriptionSyncInFlightRef.current) {
        return;
      }
      subscriptionSyncInFlightRef.current = true;
      try {
        let state = getPushState();
        // Other browsers may still prompt without a tap. iOS never does, so
        // there PushPrompt asks from a button instead.
        if (state === "default" && !isIOS()) {
          state = await requestPushPermission();
        }
        if (state === "granted") {
          await syncPushSubscription(axiosPrivate, auth.user);
        }
      } catch (err) {
        console.error("Failed to register push subscription", err);
      } finally {
        subscriptionSyncInFlightRef.current = false;
      }
    };

    registerForPushNotifications();
  }, [auth?.user, axiosPrivate]);

  const applyLatestSummary = useCallback(
    (data) => {
      setCredits(data?.credits ?? 0);
      setChecking(Array.isArray(data?.checking) ? data.checking : []);
      const unseen = Number(data?.unseen_count ?? 0);
      localStorage.setItem("notif", unseen);
      setNotifLength(unseen);
      if (data?.revision) {
        const revisionChanged =
          latestSummaryRevisionRef.current !== null &&
          latestSummaryRevisionRef.current !== data.revision;
        latestSummaryRevisionRef.current = data.revision;
        return { revisionChanged, revision: data.revision };
      }
      return { revisionChanged: false, revision: null };
    },
    [setChecking, setCredits, setNotifLength]
  );

  const applyLatestUpdates = useCallback(
    (data) => {
      setUpdates(Array.isArray(data?.updates) ? data.updates : []);
      setCredits(data?.credits ?? 0);
      setChecking(Array.isArray(data?.checking) ? data.checking : []);
      const unseen =
        data?.unseen_count ??
        data?.updates?.filter((r) => r.seen !== 1)?.length ??
        0;
      localStorage.setItem("notif", unseen);
      setNotifLength(unseen);
      if (data?.revision) {
        latestSummaryRevisionRef.current = data.revision;
      }
    },
    [setChecking, setCredits, setNotifLength]
  );

  const fetchLatestSummary = useCallback(async () => {
    if (!auth?.user) return null;

    if (summaryRequestRef.current) {
      return summaryRequestRef.current;
    }

    if (summaryAbortRef.current) {
      summaryAbortRef.current.abort();
    }

    const controller = new AbortController();
    summaryAbortRef.current = controller;

    const request = (async () => {
      try {
        const response = await axiosPrivate.get("/updates/latest/summary", {
          signal: controller.signal,
          headers: {
            "Cache-Control": "no-cache",
          },
        });
        return applyLatestSummary(response.data);
      } catch (err) {
        console.error(err);
        if (!axios.isCancel(err)) {
          navigate("/login", { state: { from: location }, replace: true });
        }
        return null;
      } finally {
        if (summaryAbortRef.current === controller) {
          summaryAbortRef.current = null;
        }
        summaryRequestRef.current = null;
      }
    })();

    summaryRequestRef.current = request;
    return request;
  }, [applyLatestSummary, auth?.user, axiosPrivate, location, navigate]);

  const fetchLatestUpdates = useCallback(async () => {
    if (!auth?.user) return;

    if (latestRequestRef.current) {
      return latestRequestRef.current;
    }

    if (latestAbortRef.current) {
      latestAbortRef.current.abort();
    }

    const controller = new AbortController();
    latestAbortRef.current = controller;
    setFetchingUpdates(true);

    const request = (async () => {
      try {
        const response = await axiosPrivate.get("/updates/latest", {
          signal: controller.signal,
          headers: {
            "Cache-Control": "no-cache",
          },
        });
        applyLatestUpdates(response.data);
      } catch (err) {
        console.error(err);
        if (!axios.isCancel(err)) {
          navigate("/login", { state: { from: location }, replace: true });
        }
      } finally {
        setFetchingUpdates(false);
        if (latestAbortRef.current === controller) {
          latestAbortRef.current = null;
        }
        latestRequestRef.current = null;
      }
    })();

    latestRequestRef.current = request;
    return request;
  }, [applyLatestUpdates, auth?.user, axiosPrivate, location, navigate]);

  useEffect(() => {
    if (!auth?.user) return;
    void fetchLatestSummary();
  }, [auth?.user, fetchLatestSummary]);

  useEffect(() => {
    if (!auth?.user) return;
    const onFocus = async () => {
      const summaryResult = await fetchLatestSummary();
      if (showNotif && (summaryResult?.revisionChanged || updates.length === 0)) {
        await fetchLatestUpdates();
      }
    };
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, [auth?.user, fetchLatestSummary, fetchLatestUpdates, showNotif, updates.length]);

  useEffect(() => {
    return () => {
      latestAbortRef.current?.abort();
      summaryAbortRef.current?.abort();
    };
  }, []);

  useInterval(
    async () => {
      if (
        auth?.user &&
        !fetchingUpdates &&
        !latestRequestRef.current &&
        !summaryRequestRef.current
      ) {
        const summaryResult = await fetchLatestSummary();
        if (showNotif && (summaryResult?.revisionChanged || updates.length === 0)) {
          await fetchLatestUpdates();
        }
      }
    },
    isVisible ? 8000 : 120000
  );
  const bell = useRef(null);
  // const location = useLocation()
  // const paths = ['/register', '/login', '/linkpage']
  return (
    <div ref={refreshCont}>
      <div
        className={`refresh-container w-fit -mt-10 m-auto ${
          pullChange > 0 ? "is-visible" : ""
        }`}
        style={{
          transform: `translateY(${pullChange / 3.2}px)`,
        }}
      >
        <div className="refresh-indicator" style={{ opacity: pullOpacity }}>
          <div className="refresh-ring">
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.8}
              stroke="currentColor"
              className="refresh-icon-svg"
              style={{
                transform: `rotate(${pullRotation}deg) scale(${pullScale})`,
              }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
          </div>
          <span className="refresh-text">{pullHint}</span>
        </div>
      </div>

      <header>
        <div className="head">
          <Link to="/">
            <p className="head-title">
              <span>MySolon</span>
            </p>
          </Link>
          <p className="head-title"></p>

          {auth?.user && (
            <div className="head-title" onClick={getSeen}>
              <FontAwesomeIcon
                ref={bell}
                title="Ενημερώσεις"
                onClick={handleShowNotif}
                icon={faBell}
              />
              <span className={notifLength === 0 ? "invisible" : "notif-count"}>
                {notifLength > 9 ? "+" : notifLength}
              </span>
              <div
                className={
                  showNotif
                    ? "notifications visible"
                    : "notifications invisible"
                }
              >
                <Notifications
                  updates={updates.filter(
                    (r) =>
                      r.court !== undefined ||
                      r.like_id !== undefined ||
                      r.comment_id !== undefined ||
                      r.periohi !== undefined ||
                      r.afm_eauction_id !== undefined ||
                      r.appeal_update !== undefined ||
                      r.pretrial_update !== undefined
                  )}
                  notifLength={notifLength}
                  handleShowNotif={handleShowNotif}
                  closeNotif={closeNotif}
                  bell={bell}
                />
              </div>
            </div>
          )}
        </div>

        <div className="head-navigation">
          {auth?.user && (
            <Link
              className={
                location.pathname === "/"
                  ? "button1-active big-screen"
                  : "button1 big-screen"
              }
              to="/"
            >
              Αρχική
            </Link>
          )}
          {auth?.user && (
            <Link
              className={
                location.pathname === "/astika"
                  ? " button1-active big-screen"
                  : "button1 big-screen"
              }
              to="/astika"
            >
              Αστικά
            </Link>
          )}
          {auth?.user && (
            <Link
              className={
                location.pathname === "/out"
                  ? " button1-active big-screen"
                  : "button1 big-screen"
              }
              to="/out"
            >
              Αστικά(Εκτός Σόλων)
            </Link>
          )}
          {auth?.user && (
            <Link
              className={
                location.pathname === "/adjustice"
                  ? "button1-active big-screen"
                  : "button1 big-screen"
              }
              to="/adjustice"
            >
              Διοικητικά
            </Link>
          )}

          {auth?.user && (
            <Link
              className={
                location.pathname === "/eauction"
                  ? "button1-active big-screen"
                  : "button1 big-screen"
              }
              to="/eauction"
            >
              Eauction
            </Link>
          )}

          {auth?.user && (
            <Link
              className={
                location.pathname.startsWith("/prothesmies")
                  ? "button1-active big-screen"
                  : "button1 big-screen"
              }
              to="/prothesmies"
            >
              Προθεσμίες
            </Link>
          )}

          {/* {auth?.user && <Link className={location.pathname === '/imerologio' ? ' button1-active big-screen' : 'button1 big-screen'} to="/imerologio">Ημερολόγιο</Link>} */}
          {auth?.user && (
            <Link
              className={
                location.pathname === "/calendar"
                  ? "button1-active big-screen"
                  : "button1 big-screen"
              }
              to="/calendar"
            >
              Ημερολόγιο
            </Link>
          )}

          {auth?.user && (
            <Link
              className={
                location.pathname === "/poinika"
                  ? "button1-active big-screen"
                  : "button1 big-screen"
              }
              to="/poinika"
            >
              Πιν.Ποινικών
            </Link>
          )}

          {/* {auth?.user && <Link className={location.pathname === '/imerologio' ? ' button1-active' : 'button1'} to="/imerologio">Ενημερώσεις</Link>} */}

          {/* {auth?.user && (location.pathname !== '/' ? <Link className="button1 big-screen" to="/">Υποθέσεις</Link> : <Link className="button1 big-screen" to="/imerologio">Ημερολόγιο</Link>)} */}
          {auth?.user && (
            <a onClick={signOut} href="/" className="button1">
              Αποσύνδεση
            </a>
          )}

          {/* <a
          className="links" href='*'
        >Οδηγίες</a
        >
        <a
          className="links" href='*'

        > Στατιστικά</a
        > */}
          {/* {auth?.user && <a onClick={signOut} href="/" class="button1">Απόσυνδεση</a>} */}
        </div>
        {/* <div className="flexGrow">

      </div> */}
      </header>

      <Link to="/">
        <img className="dot" src="/images/logo.jpeg" alt="logo" />
      </Link>

      <div className={classes} id="user-header">
        {auth?.user && (
          <section className={user_classes}>
            <Link to={`/profile/${auth?.uuid}`} className="header-user-line">
              <HeaderAvatar
                uuid={auth?.uuid}
                avatarUploaded={avatarUploaded}
                avatar={avatar}
                userAvatar={auth?.avatar}
                avatarClass={"sm"}
              />
              {auth?.user}
            </Link>
            <CashIcon height={"1rem"} />{" "}
            <FlipNumbers
              height={12}
              width={9}
              color="#469a7f"
              background="#f1f1f1"
              play
              perspective={100}
              numberStyle={{
                textIndent: "0px",
              }}
              numbers={credits >= 0 ? String(credits) : String(0)}
            />
          </section>
        )}
      </div>
      {auth?.user && (
        <div className="extra-menu">
          <Link
            className={location.pathname === "/" ? "button1-active" : "button1"}
            to="/"
          >
            Αρχική
          </Link>
          <Link
            className={
              location.pathname === "/astika" ? " button1-active" : "button1"
            }
            to="/astika"
          >
            Αστικά
          </Link>
          <Link
            className={
              location.pathname === "/adjustice" ? "button1-active" : "button1"
            }
            to="/adjustice"
          >
            Διοικητικά
          </Link>

          <Link
            className={
              location.pathname === "/eauction" ? "button1-active" : "button1"
            }
            to="/eauction"
          >
            Eauction
          </Link>
        </div>
      )}

      {auth?.user && (
        <div className="extra-menu">
          {/* <Link className={location.pathname === '/imerologio' ? ' button1-active' : 'button1'} to="/imerologio">Ημερολόγιο</Link> */}
          <Link
            className={
              location.pathname === "/calendar" ? "button1-active" : "button1"
            }
            to="/calendar"
          >
            Ημερολόγιο
          </Link>

          <Link
            className={
              location.pathname.startsWith("/prothesmies")
                ? "button1-active"
                : "button1"
            }
            to="/prothesmies"
          >
            Προθεσμίες
          </Link>

          <div>
            <Link
              className={
                ["/poinika", "/out", "/case-stats"].includes(location.pathname)
                  ? "button1-active"
                  : "button1"
              }
              onClick={(e) => {
                e.preventDefault();
                setShowExtraMenu(!showExtraMenu);
              }}
            >
              Επιπλέον
            </Link>

            {showExtraMenu && (
              <div style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: '#469a7f',
                borderRadius: '0.375rem',
                minWidth: '250px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Link
                  className={
                    location.pathname === "/out"
                      ? "button1-active"
                      : "button1"
                  }
                  to="/out"
                  onClick={() => setShowExtraMenu(false)}
                  style={{
                    borderTopLeftRadius: '0.375rem',
                    borderTopRightRadius: '0.375rem'
                  }}
                >
                  Αστικά (Εκτός Σόλων)
                </Link>
                <Link
                  className={
                    location.pathname === "/poinika"
                      ? "button1-active"
                      : "button1"
                  }
                  to="/poinika"
                  onClick={() => setShowExtraMenu(false)}
                >
                  Πιν.Ποινικών
                </Link>
                <Link
                  className={
                    location.pathname === "/case-stats"
                      ? "button1-active"
                      : "button1"
                  }
                  to="/case-stats"
                  onClick={() => setShowExtraMenu(false)}
                  style={{
                    borderBottomLeftRadius: '0.375rem',
                    borderBottomRightRadius: '0.375rem'
                  }}
                >
                  Στατιστικά
                </Link>
              </div>
            )}

            {showExtraMenu && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 999
                }}
                onClick={() => setShowExtraMenu(false)}
              />
            )}
          </div>

          {/* <Link className={location.pathname === '/etairies' ? 'button1-active' : 'button1'} to="/etairies">Εταιρίες</Link>
      <Link className={location.pathname === '/' ? ' button1-active' : 'button1'} to="/">Υποθέσεις</Link>
      <Link className={location.pathname === '/imerologio' ? ' button1-active' : 'button1'} to="/imerologio">Ημερολόγιο</Link> */}
        </div>
      )}
    </div>
  );
};

export default Header;
